import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { FiUpload, FiCheck, FiPlus, FiTrash2 } from "react-icons/fi";
import { compressImage } from "../../firebase/upload";

const defaultPillars = [
  { title: "Fostering Social Connections For Mental Health", description: "We prioritize the cultivation of meaningful social connections to enhance mental well-being and build a supportive community." },
  { title: "Encouraging Kindness And Generosity", description: "We advocate for acts of kindness and generosity, believing that small gestures can make a significant impact on individuals and society." },
  { title: "Promoting Compassion For Social Change", description: "We champion compassion as a driving force for positive social change, striving to create a more empathetic and inclusive world." },
  { title: "Advancing Holistic Self-Care", description: "We emphasize the importance of comprehensive self-care, encompassing mental, emotional, physical, environmental, spiritual, and social aspects, to foster overall well-being." },
];

const defaultContent = {
  bannerImage: "",
  welcomeHeading: "Welcome to Simple Kindness!",
  introParagraphs: "I'm so glad you're here. I founded Simple Kindness in May 2024 to bridge the gap in resources and education around mental health, inspired by the many individuals and families who often lack awareness of available support and how to access services. Together, we can create more meaningful change and ensure there are no \"faces\" that struggle in isolation.\n\n\"Let's continue breaking the silence. My advocacy has written for the promise to give a voice to those who need it most.\"\n\nI hope this starts ongoing, open conversations about mental health, moving past stigma and toward understanding and support for everyone. Many of us have personal or family experiences with mental health issues, and together, we can build a compassionate community where everyone feels safe and heard.\n\nI stand against silence and support turning for mental health awareness. If you feel comfortable, I encourage you to share your story so that we can uplift each other.\n\nThank you for being part of this journey toward kindness and understanding.",
  signatureImage: "",
  authorImage: "",
  authorFirstName: "Kristin Schmidt",
  authorLastName: "Larson",
  authorTitle: "Founder of Simple Kindness",
  linkedinText: "Follow me on LinkedIn",
  logoMarkImage: "",
  missionHeading: "Mission Statement",
  missionQuote: "\u201cOne act of kindness can change the world.\u201d",
  pillarsHeading: "Our Four Pillars",
  pillars: defaultPillars,
  importantNote: "For each product sold, 2% will be donated to the National Alliance on Mental Illness (NAMI) \u2014 the nation\u2019s largest health organization dedicated to building better lives for millions of Americans affected by mental health.",
  subscribeHeading: "Get The Latest Updates",
  subscribePlaceholder: "Enter your email",
  subscribeButtonText: "Subscribe",
};

const HomeContent = () => {
  const [content, setContent] = useState(defaultContent);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "homeContent"));
        if (snap.exists()) {
          const data = snap.data();
          setContent((prev) => ({
            ...prev,
            ...data,
            pillars: data.pillars || defaultPillars,
          }));
        }
      } catch (e) {}
      setLoading(false);
    };
    fetch();
  }, []);

  const handleUpload = async (e, field, maxW = 800, quality = 0.5) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading((u) => ({ ...u, [field]: true }));
    try {
      const base64 = await compressImage(file, maxW, quality);
      setContent((s) => ({ ...s, [field]: base64 }));
      await setDoc(doc(db, "settings", "homeContent"), { [field]: base64 }, { merge: true });
    } catch (e) { alert("Upload failed: " + e.message); }
    setUploading((u) => ({ ...u, [field]: false }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await setDoc(doc(db, "settings", "homeContent"), content, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { alert("Save failed: " + e.message + "\n\nTry clicking 'Reset All Data' first."); }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm("Reset ALL home page data to defaults? This will remove all uploaded images.")) return;
    setSaving(true);
    try {
      await deleteDoc(doc(db, "settings", "homeContent"));
      await setDoc(doc(db, "settings", "homeContent"), defaultContent);
      setContent(defaultContent);
      alert("Document reset successfully!");
    } catch (e) { alert("Reset failed: " + e.message); }
    setSaving(false);
  };

  const updatePillar = (i, field, val) => {
    const p = [...content.pillars];
    p[i] = { ...p[i], [field]: val };
    setContent((s) => ({ ...s, pillars: p }));
  };

  const addPillar = () => {
    setContent((s) => ({ ...s, pillars: [...s.pillars, { title: "", description: "" }] }));
  };

  const removePillar = (i) => {
    setContent((s) => ({ ...s, pillars: s.pillars.filter((_, idx) => idx !== i) }));
  };

  const UploadField = ({ label, field, preview, maxW = 800, quality = 0.5 }) => (
    <div className="bg-gray-50 rounded-xl p-5">
      <label className="block text-[12px] font-bold text-gray-600 mb-3">{label}</label>
      {content[field] && (
        <img src={content[field]} alt={label} className="h-20 w-auto object-contain mb-3 bg-white rounded border p-1" />
      )}
      {!content[field] && preview === "banner" && (
        <div className="h-20 bg-gray-200 rounded mb-3 flex items-center justify-center text-[11px] text-gray-400 border-2 border-dashed">Banner Image</div>
      )}
      {!content[field] && preview === "small" && (
        <div className="h-16 w-16 bg-gray-200 rounded mb-3 flex items-center justify-center text-[11px] text-gray-400 border-2 border-dashed">Image</div>
      )}
      <label className="flex items-center gap-2 cursor-pointer w-fit px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#e84fa5] text-[13px] text-gray-500 hover:text-[#e84fa5] transition">
        <FiUpload size={14} />
        {uploading[field] ? "Uploading..." : `Upload ${label}`}
        <input type="file" accept="image/*" onChange={(e) => handleUpload(e, field, maxW, quality)} className="hidden" disabled={uploading[field]} />
      </label>
    </div>
  );

  if (loading) {
    return <AdminLayout><div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="mb-6">
          <h1 className="text-2xl font-extrabold text-black">Home Page Content</h1>
          <p className="text-sm text-gray-400">Manage all content displayed on the homepage</p>
        </div>

        <form onSubmit={handleSave} className="space-y-8">
          {/* Banner */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Top Banner</h2>
            <UploadField label="Banner Image" field="bannerImage" preview="banner" maxW={1200} quality={0.5} />
          </div>

          {/* Intro Section */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Intro Section</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Welcome Heading</label>
                <input value={content.welcomeHeading} onChange={(e) => setContent((s) => ({ ...s, welcomeHeading: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Intro Paragraphs (use \n\n for paragraph breaks)</label>
                <textarea value={content.introParagraphs} onChange={(e) => setContent((s) => ({ ...s, introParagraphs: e.target.value }))} rows={8}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-y" />
              </div>
              <UploadField label="Signature Image" field="signatureImage" preview="small" maxW={200} quality={0.4} />
              <UploadField label="Author Image" field="authorImage" preview="small" maxW={300} quality={0.5} />
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Author First Name</label>
                <input value={content.authorFirstName} onChange={(e) => setContent((s) => ({ ...s, authorFirstName: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Author Last Name</label>
                <input value={content.authorLastName} onChange={(e) => setContent((s) => ({ ...s, authorLastName: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Author Title</label>
                <input value={content.authorTitle} onChange={(e) => setContent((s) => ({ ...s, authorTitle: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">LinkedIn Text</label>
                <input value={content.linkedinText} onChange={(e) => setContent((s) => ({ ...s, linkedinText: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <UploadField label="Logo Mark Image" field="logoMarkImage" preview="small" maxW={200} quality={0.4} />
            </div>
          </div>

          {/* Mission Section */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Mission Section</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Mission Heading</label>
                <input value={content.missionHeading} onChange={(e) => setContent((s) => ({ ...s, missionHeading: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Mission Quote</label>
                <input value={content.missionQuote} onChange={(e) => setContent((s) => ({ ...s, missionQuote: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Pillars Heading</label>
                <input value={content.pillarsHeading} onChange={(e) => setContent((s) => ({ ...s, pillarsHeading: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
            </div>

            {/* Pillars */}
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-[12px] font-bold text-gray-600">Four Pillars</label>
                <button type="button" onClick={addPillar}
                  className="flex items-center gap-1 text-[12px] text-[#e84fa5] font-semibold hover:text-pink-700 transition">
                  <FiPlus size={14} /> Add Pillar
                </button>
              </div>
              <div className="space-y-4">
                {content.pillars.map((p, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-bold text-gray-400">Pillar {i + 1}</span>
                      {content.pillars.length > 1 && (
                        <button type="button" onClick={() => removePillar(i)} className="text-red-400 hover:text-red-600 transition">
                          <FiTrash2 size={14} />
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-3">
                      <input value={p.title} onChange={(e) => updatePillar(i, "title", e.target.value)}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="Pillar title" />
                      <textarea value={p.description} onChange={(e) => updatePillar(i, "description", e.target.value)} rows={2}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#e84fa5] resize-y" placeholder="Pillar description" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Important Note */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Important Note</h2>
            <textarea value={content.importantNote} onChange={(e) => setContent((s) => ({ ...s, importantNote: e.target.value }))} rows={3}
              className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-y" />
          </div>

          {/* Subscribe Section */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Subscribe Section</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Heading</label>
                <input value={content.subscribeHeading} onChange={(e) => setContent((s) => ({ ...s, subscribeHeading: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Input Placeholder</label>
                <input value={content.subscribePlaceholder} onChange={(e) => setContent((s) => ({ ...s, subscribePlaceholder: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Button Text</label>
                <input value={content.subscribeButtonText} onChange={(e) => setContent((s) => ({ ...s, subscribeButtonText: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button type="submit" disabled={saving}
              className="px-8 py-3 bg-[#e84fa5] text-white font-bold text-[13px] uppercase rounded-lg hover:bg-pink-600 transition disabled:opacity-60">
              {saving ? "Saving..." : "Save Home Page"}
            </button>
            {saved && (
              <span className="flex items-center gap-2 text-green-600 text-[13px] font-semibold">
                <FiCheck /> Home page saved!
              </span>
            )}
            <button type="button" onClick={handleReset} disabled={saving}
              className="px-4 py-2 text-[11px] text-red-500 border border-red-300 rounded-lg hover:bg-red-50 transition font-semibold">
              Reset All Data
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default HomeContent;
